import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Table } from "../../components";
import { useReactToPrint } from "react-to-print";
import { addCommas, useRequest } from "../../utils";
import { products_img_url } from "../../utils/imgBaseUrl";
import { ComponentToPrint } from "../../components/ComponentToPrint/ComponentToPrint";
import {
  printProformaHtml,
  downloadProformaPdf,
} from "../../utils/proformaDownload";
import styles from "../getOrders/OrdersPanel.module.css";

const DASH_API = "https://dashboardapi.pipeup.ir";

/** Display-only; same calendar/digits/layout as GetQuotes (local copy). */
function formatFaDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fa-IR");
}

/**
 * Order details — GetQuotes-like sections; preserves all existing APIs/actions.
 * Isolated workspace copy — not deployed.
 */
const OrderDetails = (props) => {
  let history = useHistory();

  const [
    get_order_details_error,
    get_order_details_loading,
    get_order_details_data,
    get_order_details_function,
  ] = useRequest({
    method: "post",
    url: "/get_order_details",
    body: { order_id: props.match.params.id },
  });

  const [
    change_payment_status_order_error,
    change_payment_status_order_loading,
    change_payment_status_order_data,
    change_payment_status_order_function,
  ] = useRequest({
    method: "post",
    url: "/change_payment_status_order",
    isFunction: true,
    body: { order_id: props.match.params.id },
  });

  const [
    change_order_status_error,
    change_order_status_loading,
    change_order_status_data,
    change_order_status_function,
  ] = useRequest({
    method: "post",
    url: "/change_order_status",
    isFunction: true,
    body: { order_id: props.match.params.id },
  });

  const [status_final, set_status_final] = useState();

  const options = [
    { title: "در انتظار تایید", value: "awaiting_approval" },
    { title: "در انتظار پرداخت", value: "awaiting_payment" },
    { title: "در حال آماده سازی", value: "preparing" },
    { title: "ارسال شد", value: "sent" },
  ];

  const data = get_order_details_data?.data;

  useEffect(() => {
    if (data) set_status_final(data.order_data.status);
  }, [data]);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  if (!data) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          {get_order_details_loading
            ? "در حال بارگذاری جزئیات…"
            : get_order_details_error
              ? "بارگذاری جزئیات ناموفق بود."
              : "در حال بارگذاری…"}
        </div>
      </div>
    );
  }

  const o = data.order_data;

  return (
    <div className={styles.root}>
      <div className={styles.pageHead}>
        <div className={styles.titleBlock}>
          <h1>جزئیات سفارش</h1>
          <p className={styles.subtitle}>کد سفارش {o.order_code}</p>
        </div>
        <div className={styles.toolbar}>
          <button type="button" className={styles.btnGhost} onClick={handlePrint}>
            پرینت
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() =>
              printProformaHtml(
                `${DASH_API}/proforma/orders/${props.match.params.id}.html`
              ).catch(() => window.alert("چاپ پیش‌فاکتور ناموفق بود"))
            }
          >
            پیش‌فاکتور (چاپ)
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() =>
              downloadProformaPdf(
                `${DASH_API}/proforma/orders/${props.match.params.id}.pdf`,
                `proforma-${o.order_code || props.match.params.id}.pdf`
              ).catch(() => window.alert("دانلود پیش‌فاکتور ناموفق بود"))
            }
          >
            دانلود PDF
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => history.push("/dashboard/getOrders")}
          >
            بازگشت به لیست
          </button>
        </div>
      </div>

      <ComponentToPrint ref={componentRef}>
        <div className={styles.detailCard}>
          <header className={styles.qHeader}>
            <div>
              <div className={styles.qRef}>{o.order_code}</div>
            </div>
            <span className={`${styles.badge} ${styles.badgeInfo}`}>
              {o.status_fa || o.status}
            </span>
          </header>

          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>اطلاعات گیرنده</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>نام</span>
                <div className={styles.infoValue}>{o.recipient_name || "—"}</div>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>موبایل</span>
                <div className={styles.infoValue} dir="ltr" style={{ textAlign: "right" }}>
                  {o.recipient_phone_number || "—"}
                </div>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>کد پستی</span>
                <div className={styles.infoValue}>{o.recipient_postal_code || "—"}</div>
              </div>
              <div className={styles.notesBlock}>
                <span className={styles.infoLabel}>آدرس</span>
                <div className={styles.infoValue}>{o.recipient_postal_address || "—"}</div>
              </div>
            </div>
          </section>

          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>جمع‌بندی مبالغ</h3>
            <div className={styles.summaryBox}>
              <div className={styles.summaryRow}>
                <span>مبلغ سفارش</span>
                <strong>{addCommas(o.price)} تومان</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>تخفیف</span>
                <strong>{addCommas(o.discount)} تومان</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>مبلغ با تخفیف</span>
                <strong>{addCommas(o.discounted_price)} تومان</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>مالیات</span>
                <strong>{addCommas(o.tax_price)} تومان</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>مبلغ نهایی</span>
                <strong>{addCommas(o.final_price)} تومان</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>نوع پرداخت</span>
                <strong>{o.payment_type === "offline" ? "آفلاین" : "آنلاین"}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>وضعیت پرداخت</span>
                <strong>{o.payment_status}</strong>
              </div>
            </div>
          </section>

          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>محصولات سفارش</h3>
            {data.order_products ? (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>شماره</th>
                      <th>عنوان / مشخصات</th>
                      <th>تعداد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.order_products.map((item, index) =>
                      item ? (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {item.logo_img?.name ? (
                                <img
                                  alt=""
                                  src={products_img_url + item.logo_img.name}
                                  style={{
                                    width: 60,
                                    height: 60,
                                    objectFit: "contain",
                                  }}
                                />
                              ) : null}
                              <div>
                                <strong>
                                  {item.title} ({item.size})
                                </strong>
                                <div style={{ color: "#64748b", fontSize: 13 }}>
                                  {(item.property || [])
                                    .map((i) => `${i.title} : ${i.description}`)
                                    .join(" · ")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{item.number}</td>
                        </tr>
                      ) : null
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>تراکنش‌های سفارش</h3>
            {data.payment_data ? (
              <Table
                Thead={() => (
                  <tr>
                    <th className="table--title">شماره</th>
                    <th className="table--title">مبلغ</th>
                    <th className="table--title">نوع پرداخت</th>
                    <th className="table--title">وضعیت</th>
                    <th className="table--title">تاریخ</th>
                  </tr>
                )}
                Tbody={() =>
                  data.payment_data.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item._price ? addCommas(item._price) + " تومان" : "-"}</td>
                      <td>{item._type === "online" ? "آنلاین" : "کارت به کارت"}</td>
                      <td>
                        {item._status === "paid"
                          ? "موفق"
                          : item._status === "waiting"
                            ? "در انتظار تایید"
                            : "ناموفق"}
                      </td>
                      <td>
                        {formatFaDateTime(item.creation_date)}
                      </td>
                    </tr>
                  ))
                }
              />
            ) : (
              <p style={{ color: "#64748b" }}>تراکنشی ثبت نشده است.</p>
            )}
          </section>

          <section className={styles.actionsBlock}>
            <h3 className={styles.detailSectionTitle}>اقدامات</h3>
            <div className={styles.actionGroupLabel}>وضعیت پرداخت</div>
            <div className={styles.actionRow}>
              {o.payment === "paid" ? (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={change_payment_status_order_loading}
                  onClick={async () => {
                    await change_payment_status_order_function({
                      order_id: props.match.params.id,
                      payment: false,
                    });
                    await get_order_details_function();
                  }}
                >
                  رد تراکنش‌ها
                </button>
              ) : null}
              {o.payment === "unpaid" || o.payment === "waiting" ? (
                <button
                  type="button"
                  className={styles.btnSuccess}
                  disabled={change_payment_status_order_loading}
                  onClick={async () => {
                    await change_payment_status_order_function({
                      order_id: props.match.params.id,
                      payment: true,
                    });
                    await get_order_details_function();
                  }}
                >
                  تایید تراکنش‌ها
                </button>
              ) : null}
            </div>

            <div className={styles.actionGroupLabel}>وضعیت ارسال</div>
            <div className={styles.actionRow}>
              <select
                className="custom-select"
                value={status_final}
                onChange={(e) => set_status_final(e.target.value)}
                style={{ maxWidth: 240, borderRadius: 10, padding: "8px 10px" }}
              >
                {options.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.title}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.btnNavy}
                disabled={change_order_status_loading}
                onClick={async () => {
                  await change_order_status_function({
                    order_id: props.match.params.id,
                    status: status_final,
                  });
                  await get_order_details_function();
                }}
              >
                اعمال وضعیت ارسال
              </button>
            </div>
            {(change_order_status_error || change_payment_status_order_error) && (
              <p style={{ color: "#c8102e" }}>خطا در ذخیره وضعیت.</p>
            )}
            {(change_order_status_data || change_payment_status_order_data) && (
              <p style={{ color: "#0d9488" }}>ذخیره شد.</p>
            )}
          </section>
        </div>
      </ComponentToPrint>
    </div>
  );
};

export { OrderDetails };
